import { html, View } from 'rune-ts';
import klass from './BadgeMainView.module.scss';
import { RankView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/RankView.ts';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { IconView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/IconView.ts';
import { differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import differenceInDays from 'date-fns/differenceInDays/index.js';
import { CardProductError } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductView.ts';

export type TProps =
  | {
      type: 'Rank';
      size: 'Large (Xlarge~Large)';
      ranking: number;
    }
  | {
      type: 'Trend';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
    }
  | {
      type: 'Popular';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
    }
  | {
      type: 'Stock_left';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
      displayAvailableStock: number;
    }
  | {
      type: 'Time_left';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Days_left';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Limited';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
      quantity: number;
    }
  | {
      type: 'Free_Shipping';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
    }
  | {
      type: 'Just_sold';
      size: 'Large (Xlarge~Large)';
      ranking?: number;
      ordered_at: string;
      currentTime: string;
    }
  | {
      type: 'Closed';
      size: 'Large (Xlarge~Large)';
    }
  | {
      type: 'Rank';
      size: 'Medium (Medium~Small)';
      ranking: number;
    }
  | {
      type: 'Trend';
      size: 'Medium (Medium~Small)';
      ranking?: number;
    }
  | {
      type: 'Popular';
      size: 'Medium (Medium~Small)';
      ranking?: number;
    }
  | {
      type: 'Stock_left';
      size: 'Medium (Medium~Small)';
      ranking?: number;
      displayAvailableStock: number;
    }
  | {
      type: 'Time_left';
      size: 'Medium (Medium~Small)';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Days_left';
      size: 'Medium (Medium~Small)';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Limited';
      size: 'Medium (Medium~Small)';
      ranking?: number;
      quantity: number;
    }
  | {
      type: 'Free_Shipping';
      size: 'Medium (Medium~Small)';
      ranking?: number;
    }
  | {
      type: 'Just_sold';
      size: 'Medium (Medium~Small)';
      ranking?: number;
      ordered_at: string;
      currentTime: string;
    }
  | {
      type: 'Closed';
      size: 'Medium (Medium~Small)';
    };

export type TBadgeMainWithoutSize =
  | {
      type: 'Rank';
      ranking: number;
    }
  | {
      type: 'Trend';
      ranking?: number;
    }
  | {
      type: 'Popular';
      ranking?: number;
    }
  | {
      type: 'Stock_left';
      ranking?: number;
      displayAvailableStock: number;
    }
  | {
      type: 'Time_left';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Days_left';
      ranking?: number;
      sell_end_at: string;
      currentTime: string;
    }
  | {
      type: 'Limited';
      ranking?: number;
      quantity: number;
    }
  | {
      type: 'Free_Shipping';
      ranking?: number;
    }
  | {
      type: 'Just_sold';
      ranking?: number;
      ordered_at: string;
      currentTime: string;
    }
  | {
      type: 'Closed';
    };

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=26351-75681&m=dev
 */
export class BadgeMainView extends View<TProps> {
  setWithoutSize(withoutSize: TBadgeMainWithoutSize) {
    this.data = { size: this.data.size, ...withoutSize };
    this.redraw();
  }

  protected override template(props: TProps) {
    if (props.type === 'Rank' && props.size === 'Large (Xlarge~Large)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}"
        >${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}</span
      >`;
    } else if (props.type === 'Trend' && props.size === 'Large (Xlarge~Large)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_trend)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::trend')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Large (Xlarge~Large)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_trend)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::popular')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Large (Xlarge~Large)') {
      const { displayAvailableStock, ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_stock_left)}">
          <span class="${classes(klass.icon, klass.size_large)}">${new IconView({ type: 'Fire' })}</span>

          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::stock_left', { count: displayAvailableStock })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Large (Xlarge~Large)') {
      const { sell_end_at, ranking, currentTime } = props;

      const hours = differenceInHours(new Date(sell_end_at), new Date(currentTime));

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(currentTime));

      let displayTime;
      if (hours >= 1) {
        displayTime = ET('card_product::badge_main::time_left_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_main::time_left_minutes', { minutes });
      } else {
        // https://www.notion.so/21853281635280b7b19dc0d13ca7994b?d=28053281635280a9b4c1001cdd72ba3d&source=copy_link#24c5328163528098b9e4cef9327c4824
        displayTime = ET('card_product::badge_main::time_left_minutes', { minutes: 1 });
      }

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_time_left)}">
          <span class="${classes(klass.icon, klass.size_large)}">${new IconView({ type: 'Fire' })}</span>

          <span class="${classes(klass.text, klass.size_large)}">
            <span class="${classes(font('14_bold'), klass.count)}">${html.preventEscape(displayTime)}</span>
          </span>
        </span>
      </span> `;
    } else if (props.type === 'Days_left' && props.size === 'Large (Xlarge~Large)') {
      const { sell_end_at, ranking, currentTime } = props;

      const days = differenceInDays(new Date(sell_end_at), new Date(currentTime));

      return html`<span class="${classes(klass.badge_main, klass.size_large)}"
        >${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_days_left)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::days_left', { days })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Limited' && props.size === 'Large (Xlarge~Large)') {
      const { quantity, ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}"
        >${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_limited)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::limited', { quantity })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Free_Shipping' && props.size === 'Large (Xlarge~Large)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_free_shipping)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('card_product::badge_main::free_shipping')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Just_sold' && props.size === 'Large (Xlarge~Large)') {
      const { ordered_at, ranking, currentTime } = props;

      const hours = differenceInHours(new Date(currentTime), new Date(ordered_at));

      const minutes = differenceInMinutes(new Date(currentTime), new Date(ordered_at));

      const seconds = differenceInSeconds(new Date(currentTime), new Date(ordered_at));

      let displayTime;
      if (hours >= 1) {
        displayTime = ET('card_product::badge_main::just_sold_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_main::just_sold_minutes', { minutes });
      } else {
        displayTime = ET('card_product::badge_main::just_sold_seconds', { seconds });
      }

      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        ${ranking ? new RankView({ size: 'Large (Xlarge~Large)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_large, klass.type_just_sold)}">
          <span class="${classes(klass.icon, klass.size_large)}">${new IconView({ type: 'Clock' })}</span>

          <span class="${classes(klass.text, klass.size_large)}">
            <span class="${classes(font('14_bold'))}"> ${displayTime} </span>

            <span class="${classes(font('14_medium'))}"
              >${ET('card_product::badge_main::just_sold_ago')}</span
            >
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Closed' && props.size === 'Large (Xlarge~Large)') {
      return html`<span class="${classes(klass.badge_main, klass.size_large)}">
        <span class="${classes(klass.badge, klass.size_large, klass.type_closed)}">
          <span class="${classes(klass.text, klass.size_large, font('14_bold'))}">
            ${ET('mps2::product::badge::closed')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Rank' && props.size === 'Medium (Medium~Small)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}"
        >${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}</span
      >`;
    } else if (props.type === 'Trend' && props.size === 'Medium (Medium~Small)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_trend)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::trend')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Popular' && props.size === 'Medium (Medium~Small)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_trend)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::popular')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Stock_left' && props.size === 'Medium (Medium~Small)') {
      const { displayAvailableStock, ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_stock_left)}">
          <span class="${classes(klass.icon, klass.size_medium)}">${new IconView({ type: 'Fire' })}</span>

          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::stock_left', { count: displayAvailableStock })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Time_left' && props.size === 'Medium (Medium~Small)') {
      const { sell_end_at, ranking, currentTime } = props;

      const hours = differenceInHours(new Date(sell_end_at), new Date(currentTime));

      const minutes = differenceInMinutes(new Date(sell_end_at), new Date(currentTime));

      let displayTime;
      if (hours >= 1) {
        displayTime = ET('card_product::badge_main::time_left_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_main::time_left_minutes', { minutes });
      } else {
        displayTime = ET('card_product::badge_main::time_left_minutes', { minutes: 1 });
      }

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null} 
        
        <span class="${classes(klass.badge, klass.size_medium, klass.type_time_left)}">
          <span class="${classes(klass.icon, klass.size_medium)}">
            ${new IconView({ type: 'Fire' })}</span
          </span>
        
          <span class="${classes(klass.text, klass.size_medium)}">
            <span class="${classes(klass.count, font('11_semibold'))}">
              ${html.preventEscape(displayTime)}
            </span>
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Days_left' && props.size === 'Medium (Medium~Small)') {
      const { sell_end_at, ranking, currentTime } = props;

      const days = differenceInDays(new Date(sell_end_at), new Date(currentTime));

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}"
        >${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_days_left)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::days_left', { days })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Limited' && props.size === 'Medium (Medium~Small)') {
      const { quantity, ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}"
        >${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_limited)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::limited', { quantity })}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Free_Shipping' && props.size === 'Medium (Medium~Small)') {
      const { ranking } = props;

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_free_shipping)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}">
            ${ET('card_product::badge_main::free_shipping')}
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Just_sold' && props.size === 'Medium (Medium~Small)') {
      const { ordered_at, ranking, currentTime } = props;

      const hours = differenceInHours(new Date(currentTime), new Date(ordered_at));

      const minutes = differenceInMinutes(new Date(currentTime), new Date(ordered_at));

      const seconds = differenceInSeconds(new Date(currentTime), new Date(ordered_at));

      let displayTime;
      if (hours >= 1) {
        displayTime = ET('card_product::badge_main::just_sold_hours', { hours });
      } else if (minutes >= 1) {
        displayTime = ET('card_product::badge_main::just_sold_minutes', { minutes });
      } else {
        displayTime = ET('card_product::badge_main::just_sold_seconds', { seconds });
      }

      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        ${ranking ? new RankView({ size: 'Medium (Medium~Small)', ranking }) : null}

        <span class="${classes(klass.badge, klass.size_medium, klass.type_just_sold)}">
          <span class="${classes(klass.icon, klass.size_medium)}">${new IconView({ type: 'Clock' })}</span>

          <span class="${classes(klass.text, klass.size_medium)}">
            <span class="${classes(font('11_bold'))}">${displayTime}</span>

            <span class="${classes(font('11_regular'))}"
              >${ET('card_product::badge_main::just_sold_ago')}</span
            >
          </span>
        </span>
      </span>`;
    } else if (props.type === 'Closed' && props.size === 'Medium (Medium~Small)') {
      return html`<span class="${classes(klass.badge_main, klass.size_medium)}">
        <span class="${classes(klass.badge, klass.size_medium, klass.type_closed)}">
          <span class="${classes(klass.text, klass.size_medium, font('11_semibold'))}"
            >${ET('mps2::product::badge::closed')}</span
          >
        </span>
      </span>`;
    } else {
      throw new CardProductError();
    }
  }
}
